import { useEffect, startTransition } from 'react';
import PageSeo from '@framework/seo/PageSeo';
import Flex from '@creditflowresearch/uilib/Flex';
import { useRouter, getQueryString, removeQueryParams } from '@framework/router';
import { getParentRoute } from '@/creditflowresearch/Router/RouteWithRedirect/getNewRoute';
import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton';
import AbsMarketChart from '@/creditflowresearch/ui/common/components/MarketChart';
import TopCompanies from '@/creditflowresearch/ui/components/TopCompanies';
import ResearchTile from '@/creditflowresearch/ui/components/research/components/ResearchTile';
import MetaSchema from '@/creditflowresearch/ui/common/components/MetaSchema';
import getParentMetaSchema from '@/creditflowresearch/ui/components/parent/getParentMetaSchema';
import { BondsTileWithFallback } from '@/creditflowresearch/ui/components/bonds/BondsTile';
import { TraceTileWithFallback } from '@/creditflowresearch/ui/components/trace/TraceTile';
import { ArticlesTilesWithFallback } from '@/creditflowresearch/ui/components/creditFlow/articles/ArticlesTiles';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import getParentBreadcrumbs from './getParentBreadcrumbs';
import { useBreadcrumbs, useMounted } from '@/creditflowresearch/ui/common/hooks';
import DealByProductTile from '@/creditflowresearch/ui/components/deals/DealTiles/DealByProductTile';
import DealTileWithFallback from '@/creditflowresearch/ui/components/deals/DealTiles/DealTileWithFallback';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import ProductPageHeader from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader';
import { useProductGradient } from '@/creditflowresearch/ui/common/hooks/products/useProductGradient';
import { RoadshowsTileWithFallback } from '@/creditflowresearch/ui/components/Roadshows/Tile';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';

import styles from './parent.module.scss';

const Parent = () => {
  const { query, asPath, replace } = useRouter();
  const isMounted = useMounted();
  const {
    company,
    checkAccessByUserRegions,
    checkAccessByUserProducts,
    regionFilters,
    productFilters,
  } = usePageFiltersContext();
  const companyName = company?.name || ''; // if we have name - it indicates that company is loaded
  const search = removeQueryParams(query, ['companyViewId', 'sponsorName']);
  const { products, regions } = query;
  const panelBackground = useProductGradient();

  const { checkAccessByUserPlan } = useUserPlanContext();
  const granted = useAccessGrant();
  // TODO: remove when the European market opening period ends
  const userAccessChecks = [
    granted || checkAccessByUserRegions(),
    granted || checkAccessByUserProducts(),
  ];

  useBreadcrumbs(
    () => companyName ? getParentBreadcrumbs({ products, regions }, companyName) : null,
    [companyName, products, regions],
  );

  useEffect(() => {
    if (!isMounted || !companyName) {
      return;
    }

    const correctRoute = getParentRoute(company);
    const pathname = asPath.split('?')[0];

    if (correctRoute !== pathname) {
      const newSearchQuery = getQueryString(search);
      startTransition(() => {
        replace(`${ correctRoute }?${ newSearchQuery }`, undefined, { shallow: true });
      });
    }
  }, [company, isMounted, companyName, asPath, search, replace]);

  return (
    <>
      { companyName && (
        <>
          <PageSeo title={ companyName } />
          <MetaSchema schema={ getParentMetaSchema(getParentRoute(company), companyName) } />
        </>
      ) }
      <ProductPageHeader
        title={ companyName }
        withoutProductTitle
        items={ [
          { key: 'regions', items: regionFilters },
          { key: 'products', items: productFilters },
        ] }
        // TODO: remove when the European market opening period ends
        accessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
        // TODO: remove when the European market opening period ends
        blockingAccessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
        panelBackground={ panelBackground }
      />
      <ArticlesTilesWithFallback userAccessChecks={ userAccessChecks } />
      <RoadshowsTileWithFallback userAccessChecks={ userAccessChecks } />
      <DealTileWithFallback userAccessChecks={ userAccessChecks }>
        <DealByProductTile />
      </DealTileWithFallback>
      <Flex direction={ { sm: 'column', md: 'column', xl: 'row' } }>
        <TopCompanies className={ styles.topIssuersWrapper } />
        <AbsMarketChart className={ styles.chartWrapper } />
      </Flex>
      <BondsTileWithFallback userAccessChecks={ userAccessChecks } />
      <TraceTileWithFallback userAccessChecks={ userAccessChecks } />
      <ResearchTile />
      <MissingSomethingModalButton />
    </>
  );
};

export default Parent;
